export interface AudienceGroupOrganization {
  id: string;
  name: string;
  orgCategory: string;
  subscription: string;
}

export interface AudienceGroup {
  id: string;
  name: string;
  description: string | null;
  memberRoleDefault: 'all' | 'org_admin';
  createdBy: string | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  organizationCount?: number;
  organizations?: AudienceGroupOrganization[];
}

export interface AudienceOption {
  audienceType: string;
  label: string;
  totalRecipients: number;
  isPredefined: boolean;
  groupId?: string;
  description?: string | null;
  organizationCount?: number;
}

export const STATIC_PREDEFINED_AUDIENCE_OPTIONS: AudienceOption[] = [
  {
    audienceType: 'calendar_email_subscribers',
    label: 'Calendar digest recipients',
    description: 'Users who would receive tonight\'s calendar digest: subscribed, with email on file, and at least one eligible workspace.',
    totalRecipients: 0,
    isPredefined: true,
  },
  {
    audienceType: 'paid_trial_org_members',
    label: 'All paid/trial organization members',
    description: 'Active members of organizations on paid or trial subscriptions (excluding citizen accounts).',
    totalRecipients: 0,
    isPredefined: true,
  },
  {
    audienceType: 'org_admins_paid_trial',
    label: 'Org admins in paid/trial organizations',
    description: 'Organization admins and system admins in paid or trial client organizations.',
    totalRecipients: 0,
    isPredefined: true,
  },
  {
    audienceType: 'selected_organizations',
    label: 'Selected organizations (one-time)',
    description: 'All active members of the organizations you select below for this send only.',
    totalRecipients: 0,
    isPredefined: true,
  },
];

export function mergeAudienceOptions(
  apiPredefined: AudienceOption[],
  apiCustomGroups: AudienceOption[],
): AudienceOption[] {
  const predefinedByType = new Map(
    apiPredefined.map((option) => [option.audienceType, option]),
  );

  const mergedPredefined = STATIC_PREDEFINED_AUDIENCE_OPTIONS
    .filter((option) => option.audienceType !== 'selected_organizations')
    .map((fallback) => {
      const fromApi = predefinedByType.get(fallback.audienceType);
      return fromApi
        ? { ...fallback, ...fromApi, isPredefined: true }
        : fallback;
    });

  const oneTime = STATIC_PREDEFINED_AUDIENCE_OPTIONS.find(
    (option) => option.audienceType === 'selected_organizations',
  );

  return [
    ...mergedPredefined,
    ...apiCustomGroups,
    ...(oneTime ? [oneTime] : []),
  ];
}

export type ClientOrganizationSearchResult = AudienceGroupOrganization;

export function normalizeClientOrganizationSearchResult(
  row: Record<string, unknown>,
): ClientOrganizationSearchResult {
  return normalizeAudienceGroupOrganization(row);
}

export function normalizeClientOrganizationSearchResults(
  rows: unknown[],
): ClientOrganizationSearchResult[] {
  return rows.map((row) => normalizeClientOrganizationSearchResult(row as Record<string, unknown>));
}

export function getAnnouncementAudienceLabel(
  audienceType: string,
  audienceConfig: Record<string, unknown> = {},
  audienceOptions: AudienceOption[] = [],
): string {
  if (audienceType === 'custom_group' && audienceConfig.group_id) {
    return getAudienceOptionLabel(
      'custom_group',
      String(audienceConfig.group_id),
      audienceOptions,
    );
  }

  return getAudienceOptionLabel(audienceType, undefined, audienceOptions);
}

export function getAudienceOptionLabel(
  audienceType: string,
  groupId?: string,
  audienceOptions: AudienceOption[] = [],
): string {
  const match = audienceOptions.find((option) => {
    if (audienceType === 'custom_group' && groupId) {
      return option.audienceType === 'custom_group' && option.groupId === groupId;
    }
    return option.audienceType === audienceType;
  });

  if (match?.label) {
    return match.label;
  }

  return STATIC_PREDEFINED_AUDIENCE_OPTIONS.find((option) => option.audienceType === audienceType)?.label
    || audienceType;
}

export function getAudienceOptionDescription(
  audienceType: string,
  audienceOptions: AudienceOption[] = [],
): string | null {
  const match = audienceOptions.find((option) => option.audienceType === audienceType);
  return match?.description
    || STATIC_PREDEFINED_AUDIENCE_OPTIONS.find((option) => option.audienceType === audienceType)?.description
    || null;
}

export type AudienceSummaryCategory = 'predefined' | 'saved_group' | 'one_time';

export interface AudienceSummary {
  audienceType: string;
  category: AudienceSummaryCategory;
  label: string;
  description: string;
  groupId?: string;
  organizationCount?: number;
  memberRoleDefault?: 'all' | 'org_admin';
}

export interface SampleRecipient {
  userId: string;
  displayName: string;
  email: string | null;
  organizationNames: string[];
}

export interface AudiencePreview {
  totalRecipients: number;
  sampleLimit: number;
  sampleRecipients: SampleRecipient[];
  audienceSummary: AudienceSummary;
}

export interface AnnouncementChannels {
  inApp: boolean;
  email: boolean;
}

export interface Announcement {
  id: string;
  title: string;
  message: string;
  audienceType: string;
  audienceConfig: Record<string, unknown>;
  channels: AnnouncementChannels;
  status: string;
  triggeredBy: string | null;
  totalRecipients: number;
  inAppCreated: number;
  emailsSent: number;
  emailsFailed: number;
  startedAt: string | null;
  completedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
  triggeredByName?: string | null;
  triggeredByEmail?: string | null;
  sendLog?: AnnouncementSendLogEntry[];
}

export interface AnnouncementSendLogEntry {
  id: string;
  userId: string;
  emailAddress: string | null;
  channel: string;
  status: string;
  errorMessage: string | null;
  sentAt: string | null;
  createdAt: string;
}

function normalizeMemberRole(value: unknown): 'all' | 'org_admin' {
  return value === 'org_admin' ? 'org_admin' : 'all';
}

export function normalizeAudienceGroupOrganization(row: Record<string, unknown>): AudienceGroupOrganization {
  return {
    id: String(row.id),
    name: String(row.name || ''),
    orgCategory: String(row.org_category || ''),
    subscription: String(row.subscription || ''),
  };
}

export function normalizeAudienceGroup(row: Record<string, unknown>): AudienceGroup {
  const organizations = Array.isArray(row.organizations)
    ? row.organizations.map((org) => normalizeAudienceGroupOrganization(org as Record<string, unknown>))
    : undefined;

  return {
    id: String(row.id),
    name: String(row.name || ''),
    description: row.description != null ? String(row.description) : null,
    memberRoleDefault: normalizeMemberRole(row.member_role_default),
    createdBy: row.created_by != null ? String(row.created_by) : null,
    isArchived: row.is_archived === true,
    createdAt: String(row.created_at || ''),
    updatedAt: String(row.updated_at || ''),
    organizationCount: row.organization_count != null ? Number(row.organization_count) : undefined,
    organizations,
  };
}

export function normalizeAudienceGroups(rows: unknown[]): AudienceGroup[] {
  return rows.map((row) => normalizeAudienceGroup(row as Record<string, unknown>));
}

function normalizeAudienceSummaryCategory(value: unknown): AudienceSummaryCategory {
  if (value === 'saved_group' || value === 'one_time') {
    return value;
  }
  return 'predefined';
}

export function normalizeAudienceSummary(row: Record<string, unknown>): AudienceSummary {
  return {
    audienceType: String(row.audience_type || ''),
    category: normalizeAudienceSummaryCategory(row.category),
    label: String(row.label || ''),
    description: String(row.description || ''),
    groupId: row.group_id != null ? String(row.group_id) : undefined,
    organizationCount: row.organization_count != null ? Number(row.organization_count) : undefined,
    memberRoleDefault: row.member_role_default === 'org_admin' ? 'org_admin' : undefined,
  };
}

export function normalizeSampleRecipient(row: Record<string, unknown>): SampleRecipient {
  const organizationNames = Array.isArray(row.organization_names)
    ? row.organization_names.map((name) => String(name))
    : [];

  return {
    userId: String(row.user_id || ''),
    displayName: String(row.display_name || ''),
    email: row.email != null ? String(row.email) : null,
    organizationNames,
  };
}

export function normalizeAudiencePreview(row: Record<string, unknown>): AudiencePreview {
  const sampleRecipients = Array.isArray(row.sample_recipients)
    ? row.sample_recipients.map((entry) => normalizeSampleRecipient(entry as Record<string, unknown>))
    : [];

  return {
    totalRecipients: Number(row.total_recipients || 0),
    sampleLimit: Number(row.sample_limit || 12),
    sampleRecipients,
    audienceSummary: normalizeAudienceSummary((row.audience_summary || {}) as Record<string, unknown>),
  };
}

export function normalizeAudienceOption(row: Record<string, unknown>): AudienceOption {
  return {
    audienceType: String(row.audience_type || ''),
    label: String(row.label || ''),
    totalRecipients: Number(row.total_recipients || 0),
    isPredefined: row.is_predefined === true,
    groupId: row.group_id != null ? String(row.group_id) : undefined,
    description: row.description != null ? String(row.description) : null,
    organizationCount: row.organization_count != null ? Number(row.organization_count) : undefined,
  };
}

export function normalizeAnnouncement(row: Record<string, unknown>): Announcement {
  const channelsRaw = (row.channels || {}) as Record<string, unknown>;

  const sendLog = Array.isArray(row.send_log)
    ? row.send_log.map((entry) => ({
        id: String((entry as Record<string, unknown>).id),
        userId: String((entry as Record<string, unknown>).user_id),
        emailAddress: (entry as Record<string, unknown>).email_address != null
          ? String((entry as Record<string, unknown>).email_address)
          : null,
        channel: String((entry as Record<string, unknown>).channel || ''),
        status: String((entry as Record<string, unknown>).status || ''),
        errorMessage: (entry as Record<string, unknown>).error_message != null
          ? String((entry as Record<string, unknown>).error_message)
          : null,
        sentAt: (entry as Record<string, unknown>).sent_at != null
          ? String((entry as Record<string, unknown>).sent_at)
          : null,
        createdAt: String((entry as Record<string, unknown>).created_at || ''),
      }))
    : undefined;

  return {
    id: String(row.id),
    title: String(row.title || ''),
    message: String(row.message || ''),
    audienceType: String(row.audience_type || ''),
    audienceConfig: (row.audience_config as Record<string, unknown>) || {},
    channels: {
      inApp: channelsRaw.in_app !== false,
      email: channelsRaw.email === true,
    },
    status: String(row.status || ''),
    triggeredBy: row.triggered_by != null ? String(row.triggered_by) : null,
    totalRecipients: Number(row.total_recipients || 0),
    inAppCreated: Number(row.in_app_created || 0),
    emailsSent: Number(row.emails_sent || 0),
    emailsFailed: Number(row.emails_failed || 0),
    startedAt: row.started_at != null ? String(row.started_at) : null,
    completedAt: row.completed_at != null ? String(row.completed_at) : null,
    errorMessage: row.error_message != null ? String(row.error_message) : null,
    createdAt: String(row.created_at || ''),
    updatedAt: String(row.updated_at || ''),
    triggeredByName: row.triggered_by_name != null ? String(row.triggered_by_name) : null,
    triggeredByEmail: row.triggered_by_email != null ? String(row.triggered_by_email) : null,
    sendLog,
  };
}

export function normalizeAnnouncements(rows: unknown[]): Announcement[] {
  return rows.map((row) => normalizeAnnouncement(row as Record<string, unknown>));
}

export function buildAudienceSelectionValue(option: AudienceOption): string {
  if (option.audienceType === 'custom_group' && option.groupId) {
    return `custom_group:${option.groupId}`;
  }
  return option.audienceType;
}

export function parseAudienceSelectionValue(value: string): {
  audienceType: string;
  audienceConfig: Record<string, unknown>;
} {
  if (value.startsWith('custom_group:')) {
    return {
      audienceType: 'custom_group',
      audienceConfig: { group_id: value.replace('custom_group:', '') },
    };
  }

  if (value === 'selected_organizations') {
    return {
      audienceType: 'selected_organizations',
      audienceConfig: { organization_ids: [] },
    };
  }

  return {
    audienceType: value,
    audienceConfig: {},
  };
}
